import { Fish, MapPin, Anchor, ChevronRight } from "lucide-react";

const categoryColors = {
  boat_ramp: 'bg-blue-500',
  trailhead: 'bg-green-500',
  bridge: 'bg-orange-500',
  parking: 'bg-purple-500',
  campground: 'bg-red-500',
  fishing_access: 'bg-orange-500',
};

export function SearchResultsDropdown({ 
  searchResults, 
  destinations,
  onSelectDestination, 
  onSelectAccessPoint, 
  onSelectLocation,
  selectedIndex = -1,
  className = ""
}) {
  if (!searchResults) return null;

  const hasResults = (
    (searchResults.destinations?.length > 0) || 
    (searchResults.access_points?.length > 0) || 
    (searchResults.locations?.length > 0)
  );

  if (!hasResults) {
    return (
      <div className={`bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden ${className}`}>
        <div className="px-4 py-6 text-center text-gray-500">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No results found</p>
        </div>
      </div>
    );
  }

  let itemIndex = 0;

  return (
    <div className={`bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden max-h-[60vh] overflow-y-auto ${className}`}>
      <div className="py-2">
        {searchResults.destinations?.length > 0 && (
          <div>
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
              Destinations
            </div>
            {searchResults.destinations.map((dest) => {
              const idx = itemIndex++;
              return (
                <button
                  key={`dest-${dest.id}`}
                  onClick={() => onSelectDestination(dest)}
                  className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left ${
                    selectedIndex === idx ? 'bg-gray-100' : ''
                  }`}
                  data-testid={`search-result-dest-${dest.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Fish className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{dest.name}</p>
                    <p className="text-sm text-gray-500 truncate">{dest.state}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              );
            })}
          </div>
        )}
        
        {searchResults.access_points?.length > 0 && (
          <div>
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
              Access Points
            </div>
            {searchResults.access_points.map((point) => {
              const idx = itemIndex++;
              return (
                <button
                  key={`ap-${point.id}`}
                  onClick={() => onSelectAccessPoint(point)}
                  className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left ${
                    selectedIndex === idx ? 'bg-gray-100' : ''
                  }`}
                  data-testid={`search-result-ap-${point.id}`}
                >
                  <div className={`w-10 h-10 rounded-full ${categoryColors[point.category] || 'bg-orange-500'} flex items-center justify-center flex-shrink-0`}>
                    <Anchor className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{point.name}</p>
                    <p className="text-sm text-gray-500 truncate">
                      {point.county && `${point.county}, `}{point.state}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              );
            })}
          </div>
        )}
        
        {searchResults.locations?.length > 0 && (
          <div>
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
              Locations
            </div>
            {searchResults.locations.map((loc, locIdx) => {
              const idx = itemIndex++;
              return (
                <button
                  key={`loc-${locIdx}`}
                  onClick={() => onSelectLocation(loc)}
                  className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left ${
                    selectedIndex === idx ? 'bg-gray-100' : ''
                  }`}
                  data-testid={`search-result-loc-${locIdx}`}
                >
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{loc.name}</p>
                    <p className="text-sm text-gray-500 truncate">{loc.full_name}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
